import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { PortalClass } from '../types';
import { portalApi } from '../utils/api';

function classTitle(item: PortalClass) {
  return `${item.class_name || item.course_code} (${item.section_code})`;
}

function StudentPortalPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const { data: currentClasses = [], isLoading: loadingCurrent } = useQuery<PortalClass[]>({
    queryKey: ['portal', 'student', 'current-classes'],
    queryFn: () => portalApi.getStudentCurrentClasses(),
  });
  const { data: pastClasses = [] } = useQuery<PortalClass[]>({
    queryKey: ['portal', 'student', 'past-classes'],
    queryFn: () => portalApi.getStudentPastClasses(),
  });
  const { data: availableClasses = [] } = useQuery<PortalClass[]>({
    queryKey: ['portal', 'student', 'available-classes'],
    queryFn: () => portalApi.getStudentAvailableClasses(),
  });

  const enrollMutation = useMutation({
    mutationFn: (sectionId: string) => portalApi.enrollInClass(sectionId),
    onSuccess: () => {
      setEnrollDialogOpen(false);
      setSelectedSection('');
      queryClient.invalidateQueries({ queryKey: ['portal', 'student'] });
    },
    onError: () => setError('Failed to enroll in class'),
  });

  const handleConfirmEnroll = () => {
    if (!selectedSection) return;
    enrollMutation.mutate(selectedSection);
  };

  const handleOpenClass = (sectionId: string) => {
    navigate(`/student-portal/classes/${sectionId}`);
  };

  if (loadingCurrent) {
    return <Typography sx={{ py: 4 }}>Loading student portal...</Typography>;
  }

  return (
    <Box sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Typography variant="h4" fontWeight={700}>Student Portal</Typography>
        {error && <Alert severity="error">{error}</Alert>}

        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Current Classes</Typography>
              <Button
                variant="contained"
                onClick={() => setEnrollDialogOpen(true)}
                disabled={availableClasses.length === 0}
              >
                Add Class
              </Button>
            </Stack>
            {currentClasses.length === 0 ? (
              <Typography color="text.secondary">No current classes enrolled.</Typography>
            ) : (
              <List>
                {currentClasses.map((item) => (
                  <ListItem
                    key={item.section_id}
                    divider
                    button
                    onClick={() => handleOpenClass(item.section_id)}
                  >
                    <ListItemText
                      primary={classTitle(item)}
                      secondary={`Click to view class details • ${item.term} • ${item.faculty_name || 'Educator N/A'}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>Past Classes</Typography>
            {pastClasses.length === 0 ? (
              <Typography color="text.secondary">No past classes available.</Typography>
            ) : (
              <List>
                {pastClasses.map((item) => (
                  <ListItem key={item.section_id} divider button onClick={() => handleOpenClass(item.section_id)}>
                    <ListItemText
                      primary={classTitle(item)}
                      secondary={`Click to view class details • ${item.term} • ${item.faculty_name || 'Educator N/A'}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={enrollDialogOpen} onClose={() => setEnrollDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Join a Class</DialogTitle>
        <DialogContent>
          {availableClasses.length === 0 ? (
            <Typography>No classes are currently available to join.</Typography>
          ) : (
            <List>
              {availableClasses.map((item) => (
                <ListItem
                  key={item.section_id}
                  button
                  selected={selectedSection === item.section_id}
                  onClick={() => setSelectedSection(item.section_id)}
                >
                  <ListItemText
                    primary={classTitle(item)}
                    secondary={`${item.term} • ${item.faculty_name || 'Educator N/A'}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleConfirmEnroll()} disabled={!selectedSection}>
            Confirm Enrollment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StudentPortalPage;
